import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  Lock,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
  SkipBack,
  SkipForward,
  UserPlus,
} from "lucide-react";
import { cn } from "../lib/cn";
import { BottomSheet } from "../components/BottomSheet";

interface CallHistoryScreenProps {
  accessToken: string;
  apiUrl: string;
  onCall: (number: string) => void;
  isActive: boolean;
}

type HistoryItem = {
  id: string;
  type: "inbound" | "outbound";
  outcome: string;
  durationSeconds: number;
  contact: { id: string | null; displayName: string; phone: string | null };
  startedAt: string;
  dispositionCode: string | null;
  hasRecording: boolean;
};

type DetailItem = HistoryItem & {
  recordingUrl: string | null;
  notes: string | null;
};

type Filter = "all" | "missed" | "incoming" | "outgoing";
type CallKind = "incoming" | "outgoing" | "missed";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "missed", label: "Missed" },
  { id: "incoming", label: "Incoming" },
  { id: "outgoing", label: "Outgoing" },
];

// Incoming = inbound + connected, Missed = inbound + not connected, Outgoing = outbound
function getCallKind(type: string, outcome: string): CallKind {
  if (type === "outbound") return "outgoing";
  if (outcome === "connected") return "incoming";
  return "missed";
}

const KIND_STYLES: Record<
  CallKind,
  { icon: React.ElementType; bg: string; text: string; textMuted: string; label: string }
> = {
  incoming: {
    icon: PhoneIncoming,
    bg: "bg-secondary",
    text: "text-secondary-foreground",
    textMuted: "text-secondary",
    label: "Incoming",
  },
  outgoing: {
    icon: PhoneOutgoing,
    bg: "bg-primary",
    text: "text-primary-foreground",
    textMuted: "text-primary",
    label: "Outgoing",
  },
  missed: {
    icon: PhoneMissed,
    bg: "bg-destructive",
    text: "text-destructive-foreground",
    textMuted: "text-destructive",
    label: "Missed",
  },
};

function formatDuration(s: number): string {
  if (s <= 0) return "Not answered";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatFullTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function outcomeLabel(outcome: string): string {
  if (outcome === "connected") return "Connected";
  if (outcome === "no_answer") return "No answer";
  if (outcome === "busy") return "Busy";
  if (outcome === "failed") return "Failed";
  if (outcome === "voicemail") return "Voicemail";
  return outcome;
}

function dispositionLabel(code: string): string {
  return code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateHeader(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function outcomeBadgeClass(outcome: string): string {
  if (outcome === "connected") return "bg-primary/10 text-primary";
  if (["no_answer", "busy", "failed", "voicemail"].includes(outcome))
    return "bg-destructive/10 text-destructive";
  return "bg-muted text-muted-foreground";
}

function formatAudioTime(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function callDescription(kind: CallKind, phone: string, startedAt: string): string {
  const time = new Date(startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const num = phone ? ` · ${phone}` : "";
  if (kind === "missed") return `${time} · No answer${num}`;
  if (kind === "incoming") return `${time}${num}`;
  return `${time}${num}`;
}

function AudioPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      a.pause();
    } else {
      void a.play();
    }
    setIsPlaying(!isPlaying);
  };

  const skip = (delta: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(a.duration, a.currentTime + delta));
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
    setCurrentTime(a.currentTime);
  };

  return (
    <div className="rounded-xl border border-border bg-card px-4 py-4">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Time labels */}
      <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatAudioTime(currentTime)}</span>
        <span>{formatAudioTime(duration)}</span>
      </div>

      {/* Progress bar */}
      <input
        type="range"
        min={0}
        max={duration || 1}
        step={0.1}
        value={currentTime}
        onChange={seek}
        className="h-1 w-full cursor-pointer"
        style={{ accentColor: "hsl(var(--primary))" }}
      />

      {/* Controls */}
      <div className="mt-4 flex items-center justify-center gap-8">
        <button
          onClick={() => skip(-10)}
          className="flex flex-col items-center gap-0.5 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Rewind 10 seconds"
        >
          <SkipBack className="h-5 w-5" />
          <span className="text-[10px]">10</span>
        </button>

        <button
          onClick={toggle}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-0.5" />}
        </button>

        <button
          onClick={() => skip(10)}
          className="flex flex-col items-center gap-0.5 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Skip 10 seconds"
        >
          <SkipForward className="h-5 w-5" />
          <span className="text-[10px]">10</span>
        </button>
      </div>
    </div>
  );
}

export function CallHistoryScreen({
  accessToken,
  apiUrl,
  onCall,
  isActive,
}: CallHistoryScreenProps) {
  const [filter, setFilter] = useState<Filter>("all");
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [detail, setDetail] = useState<DetailItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Reset to list when user navigates away from this tab
  useEffect(() => {
    if (!isActive) setDetail(null);
  }, [isActive]);

  const [saveContactFor, setSaveContactFor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveForm, setSaveForm] = useState({ firstName: "", lastName: "", email: "" });

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${apiUrl}/dashboard/call-history?limit=50`;
      if (filter === "missed") url += "&direction=inbound&outcome=no_answer";
      else if (filter === "incoming") url += "&direction=inbound";
      else if (filter === "outgoing") url += "&direction=outbound";

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error("Failed to load");
      const data = (await res.json()) as { items: HistoryItem[] };
      setItems(data.items ?? []);
    } catch {
      setError("Couldn't load call history.");
    } finally {
      setLoading(false);
    }
  }, [filter, accessToken, apiUrl]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const fetchDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${apiUrl}/dashboard/call-history/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error("Failed");
      const data = (await res.json()) as DetailItem;
      setDetail(data);
    } catch {
      // stay on list
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveContact = async () => {
    if (!saveContactFor) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/contacts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: saveForm.firstName.trim() || null,
          lastName: saveForm.lastName.trim() || null,
          email: saveForm.email.trim() || null,
          phone: saveContactFor,
        }),
      });
      if (res.ok) {
        setSaveContactFor(null);
        setSaveForm({ firstName: "", lastName: "", email: "" });
        void fetchList();
      }
    } catch {
      // noop
    } finally {
      setSaving(false);
    }
  };

  // ── Detail sub-screen — full PostCallScreen-style overlay ───────────────────
  if (detail) {
    const phone = detail.contact?.phone ?? "";
    const name = detail.contact?.displayName ?? phone;
    const kind = getCallKind(detail.type, detail.outcome);
    const kindStyle = KIND_STYLES[kind];
    const KindIcon = kindStyle.icon;
    const hasSavedContact = !!detail.contact?.id;

    return (
      <div className="flex flex-1 flex-col bg-background">
        {/* Back button */}
        <div className="flex items-center px-3 pt-3">
          <button
            onClick={() => setDetail(null)}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>

        {/* Hero */}
        <div className="flex flex-col items-center justify-center gap-2 px-6 py-6">
          {/* Kind icon circle — solid color matching list row, larger */}
          <div
            className={cn(
              "mb-1 flex h-14 w-14 items-center justify-center rounded-full shadow-sm",
              kindStyle.bg,
            )}
          >
            <KindIcon className={cn("h-7 w-7", kindStyle.text)} />
          </div>

          {/* Kind label in matching color */}
          <p className={cn("text-xs font-medium", kindStyle.textMuted)}>{kindStyle.label}</p>

          {/* Contact name */}
          <p className="text-2xl font-semibold tracking-tight text-foreground">{name}</p>

          {/* Phone (only shown if contact has a saved name) */}
          {hasSavedContact && phone && name !== phone && (
            <p className="text-sm text-muted-foreground">{phone}</p>
          )}

          {/* Date · Time */}
          <p className="text-xs text-muted-foreground">
            {formatFullDate(detail.startedAt)} · {formatFullTime(detail.startedAt)}
          </p>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
          <div className="space-y-5">
            {/* Call info */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Call info
              </p>
              <div className="overflow-hidden rounded-xl border border-border bg-card divide-y divide-border">
                {/* Date */}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-muted-foreground">Date</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatFullDate(detail.startedAt)}
                  </span>
                </div>
                {/* Status — colored badge */}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                      outcomeBadgeClass(detail.outcome),
                    )}
                  >
                    {outcomeLabel(detail.outcome)}
                  </span>
                </div>
                {/* Duration — only for connected calls */}
                {detail.durationSeconds > 0 && (
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">Duration</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatDuration(detail.durationSeconds)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Disposition */}
            {detail.dispositionCode && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Disposition
                </p>
                <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground">
                  {dispositionLabel(detail.dispositionCode)}
                </span>
              </div>
            )}

            {/* Recording */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Recording
              </p>
              {!detail.hasRecording ? (
                <p className="text-sm text-muted-foreground">No recording for this call.</p>
              ) : detail.recordingUrl ? (
                <AudioPlayer src={detail.recordingUrl} />
              ) : (
                <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                  <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Unavailable on your current plan.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Group items by date for the list view ──────────────────────────────────
  const grouped: { date: string; calls: HistoryItem[] }[] = [];
  for (const item of items) {
    const date = formatDateHeader(item.startedAt);
    const last = grouped[grouped.length - 1];
    if (last?.date === date) {
      last.calls.push(item);
    } else {
      grouped.push({ date, calls: [item] });
    }
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Filter pills */}
      <div className="flex shrink-0 gap-2 overflow-x-auto px-4 py-3">
        {FILTERS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={cn(
              "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors",
              filter === id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <Phone className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No calls found.</p>
          </div>
        ) : (
          <div className="space-y-4 py-3">
            {grouped.map(({ date, calls }) => (
              <div key={date}>
                <p className="px-4 pb-2 text-xs font-medium text-muted-foreground">{date}</p>
                <div className="space-y-1.5 px-4">
                  {calls.map((item) => {
                    const phone = item.contact?.phone ?? "";
                    const name = item.contact?.displayName ?? phone;
                    const isUnsaved = !item.contact?.id;
                    const kind = getCallKind(item.type, item.outcome);
                    const { icon: KindIcon, bg, text } = KIND_STYLES[kind];

                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                          if (detailLoading) return;
                          void fetchDetail(item.id);
                        }}
                        className="group flex cursor-pointer items-center gap-3 rounded-xl bg-card px-4 py-3 shadow-sm transition-shadow hover:shadow-md"
                      >
                        {/* Call kind icon */}
                        <div
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                            bg,
                          )}
                        >
                          <KindIcon className={cn("h-5 w-5", text)} />
                        </div>

                        {/* Middle */}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-foreground">{name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {callDescription(kind, phone, item.startedAt)}
                          </p>
                        </div>

                        {/* Right — action buttons appear on hover */}
                        <div
                          className="flex shrink-0 items-center gap-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Call button */}
                          <button
                            onClick={() => onCall(phone)}
                            className="rounded-full bg-primary/10 p-1.5 text-primary opacity-0 transition-opacity group-hover:opacity-100 hover:bg-primary/20"
                            aria-label="Call"
                          >
                            <Phone className="h-3.5 w-3.5" />
                          </button>
                          {/* Save contact — always rendered; invisible for saved contacts */}
                          <button
                            onClick={() => {
                              setSaveContactFor(phone);
                              setSaveForm({ firstName: "", lastName: "", email: "" });
                            }}
                            className={cn(
                              "rounded-full bg-primary/10 p-1.5 text-primary opacity-0 transition-opacity group-hover:opacity-100 hover:bg-primary/20",
                              !isUnsaved && "invisible pointer-events-none",
                            )}
                            aria-label="Save contact"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add contact BottomSheet */}
      <BottomSheet
        open={saveContactFor !== null}
        onClose={() => setSaveContactFor(null)}
        title="Save contact"
      >
        <div className="space-y-3 px-5 pb-6">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Phone</label>
            <input
              readOnly
              value={saveContactFor ?? ""}
              className="w-full rounded-xl border border-border bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              First name
            </label>
            <input
              value={saveForm.firstName}
              onChange={(e) => setSaveForm((f) => ({ ...f, firstName: e.target.value }))}
              placeholder="First name"
              className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Last name
            </label>
            <input
              value={saveForm.lastName}
              onChange={(e) => setSaveForm((f) => ({ ...f, lastName: e.target.value }))}
              placeholder="Last name"
              className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Email (optional)
            </label>
            <input
              type="email"
              value={saveForm.email}
              onChange={(e) => setSaveForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="email@example.com"
              className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            onClick={() => void handleSaveContact()}
            disabled={saving}
            className="mt-1 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save contact"}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
